import classnames from 'classnames';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { connectInfiniteHits } from 'react-instantsearch-dom';
import Masonry from 'react-masonry-css';

import { IconNavigationArrowChevronNormalUp } from '../../../components/Icons';
import Board from './featuredImages/board.svg';
import Chip from './featuredImages/chip.svg';
import Clip from './featuredImages/clip.svg';
import Cloud from './featuredImages/cloud.svg';
import Crane from './featuredImages/crane.svg';
import Gear from './featuredImages/gear.svg';
import Led from './featuredImages/led.svg';
import Light from './featuredImages/light.svg';
import LinkIcon from './featuredImages/link.svg';
import Web from './featuredImages/web.svg';
import * as css from './TutorialHits.module.scss';

const featuredImages = {
  led: Led,
  chip: Chip,
  board: Board,
  clip: Clip,
  cloud: Cloud,
  crane: Crane,
  gear: Gear,
  light: Light,
  link: LinkIcon,
  web: Web,
};

// This is a custom effect that calls onResize when page load and on window resize
const useResizeEffect = (onResize, deps = []) => {
  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [deps, onResize]);
};

const TutorialHit = ({ hit }) => {
  const tagsWrapperRef = useRef(null);
  const [tags, setTags] = useState(hit.tags);
  const [collapsedTags, setCollapsedTags] = useState([]);
  const [showHiddenTags, setShowHiddenTags] = useState(false);
  const cardRef = useRef(null);

  const updateTags = () => {
    if (!tagsWrapperRef.current || !tagsWrapperRef.current.scrollWidth || !cardRef.current.offsetWidth || tags.length < 2) {
      return;
    }
    if (tagsWrapperRef.current.scrollWidth > cardRef.current.offsetWidth - 55) {
      setCollapsedTags([[...tags].pop(), ...collapsedTags]);
      setTags(tags.slice(0, -1));
    }
  };

  useResizeEffect(updateTags);

  const handleExpand = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowHiddenTags(true);
  };

  return (
    <Link className={classnames(css.card, { [css.featured]: hit.featuredImage })} to={hit.slug} ref={cardRef}>
      {hit.featuredImage && (
        <div className={css.featuredImageWrapper}>
          <img src={featuredImages[hit.featuredImage]} alt="featured logo" />
        </div>
      )}
      {hit.difficulty && <span className={classnames(css.difficulty, { [css[hit.difficulty]]: !hit.featuredImage })}>{hit.difficulty}</span>}
      <span className={css.title} to={hit.slug}>
        {hit.title}
      </span>
      {hit.tags && (
        <div className={classnames(css.tagsWrapper, { [css.wrap]: showHiddenTags })} ref={tagsWrapperRef}>
          {tags.map((arch) => {
            return (
              <div className={css.tag} key={arch}>
                {arch}
              </div>
            );
          })}
          {collapsedTags.length > 0 && (
            <div className={classnames(css.tag, css.plus, { [css.hidden]: showHiddenTags })} onClick={handleExpand}>
              +{collapsedTags.length}
            </div>
          )}
          {collapsedTags.map((arch) => {
            return (
              <div className={classnames(css.tag, { [css.hidden]: !showHiddenTags })} key={arch}>
                {arch}
              </div>
            );
          })}
        </div>
      )}
    </Link>
  );
};

TutorialHit.propTypes = {
  hit: PropTypes.object,
};

const TutorialHits = ({ hits, hasMore, refineNext }) => {
  const ref = useRef();

  // Use Observer API to detect end of the list and retrieve new results if available
  useEffect(() => {
    const node = ref && ref.current; // DOM Ref
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || !node) return;

    const onSentinelIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMore) {
          refineNext();
        }
      });
    };

    const observer = new IntersectionObserver(onSentinelIntersection);

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, ref, refineNext]);

  const breakpointColumnsObj = {
    default: 4,
    1024: 3,
    768: 2,
    480: 1,
  };

  return (
    <>
      <Masonry breakpointCols={breakpointColumnsObj} className={css.masonryGrid} columnClassName="tutorial-masonry-grid_column">
        {hits.map((hit) => (
          <TutorialHit hit={hit} key={hit.slug} />
        ))}
      </Masonry>
      {hits && <div style={{ width: '100%' }} ref={ref}></div>}
      {!hasMore && (
        <div className={css.backToTop}>
          <span>{'That’s all for now!'}</span>
          <a href="#top">
            {'BACK TO TOP'}{' '}
            <div>
              <IconNavigationArrowChevronNormalUp />
            </div>
          </a>
        </div>
      )}
    </>
  );
};

TutorialHits.propTypes = {
  hits: PropTypes.array.isRequired,
  hasMore: PropTypes.bool,
  refineNext: PropTypes.func,
};

export default connectInfiniteHits(TutorialHits);
